import { AxiosError, AxiosRequestConfig } from 'axios';

/** Что известно о сбое запроса к API: прикладывается к обращению в поддержку. */
export interface ApiFailure {
  page: string;
  method: string;
  url: string;
  status: number | null;
  response: string;
  requestId: string;
}

type Listener = (failure: ApiFailure | null) => void;

// Те же пределы проверяет бэкенд: значение длиннее не даст отправить обращение.
const URL_LIMIT = 2000;
const RESPONSE_LIMIT = 2000;
const REQUEST_ID_LIMIT = 64;

const SUPPORT_PATH = '/support/';

let lastFailure: ApiFailure | null = null;
const listeners = new Set<Listener>();

const notify = () => listeners.forEach((listener) => listener(lastFailure));

const requestUrl = ({ baseURL = '', url = '' }: AxiosRequestConfig) => `${baseURL}${url}`;

const responseText = (error: AxiosError) => {
  if (!error.response) return error.message || '';

  const { data } = error.response;

  if (typeof data === 'string') return data;
  // Тело скачиваемого файла текста ошибки не несёт.
  if (data instanceof Blob || data instanceof ArrayBuffer) return '';

  return JSON.stringify(data) || '';
};

// Сбой системы: сервер не ответил или ответил 5xx. Ошибки валидации и доступа — не сбои, отменённый
// запрос приходит без config. Запросы самой поддержки не считаются: её отказ подменил бы сбой,
// о котором человек сообщает.
export const isSystemFailure = (error: AxiosError): boolean =>
  Boolean(error.config) &&
  (error.config.url || '').indexOf(SUPPORT_PATH) !== 0 &&
  (!error.response || error.response.status >= 500);

export const rememberFailure = (error: AxiosError): void => {
  const { config, request, response } = error;

  lastFailure = {
    page: globalThis.location.href.slice(0, URL_LIMIT),
    method: (config.method || '').toUpperCase(),
    url: (request?.responseURL || requestUrl(config)).slice(0, URL_LIMIT),
    status: response ? response.status : null,
    response: responseText(error).slice(0, RESPONSE_LIMIT),
    requestId: (response?.headers['x-request-id'] || '').slice(0, REQUEST_ID_LIMIT),
  };
  notify();
};

export const clearFailure = (): void => {
  lastFailure = null;
  notify();
};

export const getLastFailure = (): ApiFailure | null => lastFailure;

export const subscribeToFailure = (listener: Listener): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};
